/**
 * Custom Post Slider
 * Handles infinite loop carousel functionality with full accessibility support
 */

/* global jQuery */

(function ($) {
  'use strict';

  if (!$('.post-slider-wrapper').length) {
    return;
  }

  function initPostSlider(sliderId, postsData) {
    const $slider = $('#' + sliderId);
    const $track = $slider.find('.slider-track');
    let $slides = $track.find('.slider-slide');
    const $wrapper = $slider.closest('.post-slider-wrapper');
    const $prevBtn = $wrapper.find('.slider-prev');
    const $nextBtn = $wrapper.find('.slider-next');

    const posts = postsData;
    const totalSlides = posts.length;

    let currentIndex = 0;
    let isAnimating = false;
    let slidesPerView = 3;
    let slideWidth = 0;
    let gap = 12;

    // ACCESSIBILITY: Add ARIA attributes on init
    function setupAccessibility() {
      // Add role and label to slider container
      $slider.attr({
        role: 'region',
        'aria-roledescription': 'carousel',
        'aria-label': 'Featured posts carousel',
        tabindex: '0',
      });

      // Add labels to navigation buttons
      $prevBtn.attr({
        'aria-label': 'Previous slide',
        'aria-controls': sliderId,
      });

      $nextBtn.attr({
        'aria-label': 'Next slide',
        'aria-controls': sliderId,
      });

      // Create live region for screen reader announcements
      if (!$wrapper.find('.slider-live-region').length) {
        $wrapper.append(
          '<div class="slider-live-region" aria-live="polite" aria-atomic="true" style="position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;"></div>'
        );
      }

      // Add keyboard instructions (visually hidden)
      if (!$wrapper.find('.slider-instructions').length) {
        $wrapper.prepend(
          '<div class="slider-instructions" style="position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;">Use left and right arrow keys to navigate slides when focused on this carousel</div>'
        );
      }
    }

    // Clone slides for infinite loop
    function createInfiniteLoop() {
      // Remove existing clones
      $track.find('.slider-slide.clone').remove();

      // Clone enough slides for seamless loop (clone all slides twice)
      const $originalSlides = $track.find('.slider-slide:not(.clone)');

      // Make original slides focusable if they don't contain focusable elements
      $originalSlides.each(function () {
        if (!$(this).find('a, button, [tabindex]').length) {
          $(this).attr('tabindex', '0');
        }
      });

      // Clone before
      for (let i = totalSlides - 1; i >= 0; i--) {
        const $clone = $originalSlides
          .eq(i)
          .clone()
          .addClass('clone clone-before')
          .attr('aria-hidden', 'true')
          .attr('tabindex', '-1'); // Remove clones from tab order

        // Remove tab order from links inside clones
        $clone.find('a, button, [tabindex]').attr('tabindex', '-1');
        $track.prepend($clone);
      }

      // Clone after
      for (let i = 0; i < totalSlides; i++) {
        const $clone = $originalSlides
          .eq(i)
          .clone()
          .addClass('clone clone-after')
          .attr('aria-hidden', 'true')
          .attr('tabindex', '-1'); // Remove clones from tab order

        // Remove tab order from links inside clones
        $clone.find('a, button, [tabindex]').attr('tabindex', '-1');
        $track.append($clone);
      }

      // Update slides reference
      updateSlideReferences();
    }

    function updateSlideReferences() {
      $slides = $track.find('.slider-slide');
    }

    function calculateDimensions() {
      const wrapperWidth = $wrapper.width();
      // Use wrapper width for calculations
      slideWidth = (wrapperWidth - gap * (slidesPerView - 1)) / slidesPerView;
      const textWidth = slideWidth + gap;

      // Set slide widths and gaps
      $slides.each(function (index) {
        $(this).css({
          width: slideWidth + 'px',
          flexShrink: 0,
        });

        // Add gap except for last slide
        if (index < $slides.length - 1) {
          $(this).css('marginRight', gap + 'px');
        }
      });

      $wrapper.find('.slider-navigation').css('max-width', textWidth + 'px');
    }

    function updatePosition(animate = true) {
      // Calculate offset to center the active slide using screen width
      const wrapperWidth = $wrapper.width();
      const actualPosition = currentIndex + totalSlides;
      const centerOffset = wrapperWidth / 2 - slideWidth / 2;
      const offset = -actualPosition * (slideWidth + gap) + centerOffset;

      if (animate) {
        $track.css({
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(${offset}px)`,
        });
      } else {
        $track.css({
          transition: 'none',
          transform: `translateX(${offset}px)`,
        });
      }

      updateActiveSlide();
      // Only update slide info when animating (visible to user)
      if (animate) {
        updateSlideInfo();
        announceSlideChange(); // ACCESSIBILITY: Announce to screen readers
      }
    }

    function updateActiveSlide() {
      $slides.removeClass('active prev-slide next-slide');

      // ACCESSIBILITY: Update aria-hidden and tabindex for all slides
      $slides.each(function () {
        $(this).attr('aria-hidden', 'true');
        // Make all focusable elements inside non-active slides unfocusable
        $(this)
          .find('a, button, [tabindex]:not([tabindex="-1"])')
          .attr('tabindex', '-1');
      });

      // Find the center slide (accounting for clones)
      const centerIndex = currentIndex + totalSlides;

      // Set active slide
      const $activeSlide = $($slides[centerIndex]);
      $activeSlide.addClass('active').attr('aria-hidden', 'false');

      // Make focusable elements in active slide focusable again
      $activeSlide.find('a, button').attr('tabindex', '0');

      // Add classes to adjacent slides for styling
      if ($slides[centerIndex - 1]) {
        $($slides[centerIndex - 1]).addClass('prev-slide');
      }
      if ($slides[centerIndex + 1]) {
        $($slides[centerIndex + 1]).addClass('next-slide');
      }

      // ACCESSIBILITY: Update aria-current
      $slides.removeAttr('aria-current');
      $activeSlide.attr('aria-current', 'true');
    }
    // ACCESSIBILITY: Announce slide changes to screen readers
    function announceSlideChange() {
      const actualIndex =
        ((currentIndex % totalSlides) + totalSlides) % totalSlides;
      const post = posts[actualIndex];

      if (post) {
        const announcement = `Slide ${actualIndex + 1} of ${totalSlides}: ${post.title}`;
        $wrapper.find('.slider-live-region').text(announcement);
      }
    }

    function updateSlideInfo() {
      const actualIndex =
        ((currentIndex % totalSlides) + totalSlides) % totalSlides;
      const post = posts[actualIndex];

      if (post) {
        const $sliderInfo = $wrapper.find('.slider-info');

        // Fade out
        $sliderInfo.css('opacity', '0');

        // Update content after fade out
        window.setTimeout(() => {
          $wrapper.find('.slide-title').text(post.title);
          $wrapper.find('.slide-title-link').attr('href', post.permalink);
          $wrapper.find('.slide-caption').text(post.excerpt);
          $wrapper.find('.post-link').attr('href', post.permalink);
          $wrapper
            .find('.post-link')
            .attr('class', 'post-link type-' + post.post_type);

          // Fade in
          $sliderInfo.css('opacity', '1');
        }, 300);
      }
    }

    function nextSlide() {
      if (isAnimating) return;
      isAnimating = true;

      currentIndex++;
      updatePosition(true);

      window.setTimeout(() => {
        // Check if we need to reset position
        if (currentIndex >= totalSlides) {
          currentIndex = currentIndex % totalSlides;
          updatePosition(false);
        }
        isAnimating = false;
      }, 600);
    }

    function prevSlide() {
      if (isAnimating) return;
      isAnimating = true;

      currentIndex--;
      updatePosition(true);

      window.setTimeout(() => {
        // Check if we need to reset position
        if (currentIndex < 0) {
          currentIndex = totalSlides + (currentIndex % totalSlides);
          updatePosition(false);
        }
        isAnimating = false;
      }, 600);
    }

    function normalizeSlideIndex(index) {
      return ((index % totalSlides) + totalSlides) % totalSlides;
    }

    /** Jump to a slide by logical index (0 … totalSlides − 1). */
    function goToSlide(targetIndex) {
      if (isAnimating) return;
      const normalized = normalizeSlideIndex(targetIndex);
      if (normalized === normalizeSlideIndex(currentIndex)) {
        return;
      }
      isAnimating = true;
      currentIndex = normalized;
      updatePosition(true);
      window.setTimeout(() => {
        isAnimating = false;
      }, 600);
    }

    // ACCESSIBILITY: Handle focus on slides to sync slider position
    function setupSlideFocusHandlers() {
      // Handle focus on original slides (not clones)
      $track.find('.slider-slide:not(.clone)').each(function (index) {
        $(this).on('focusin', function () {
          // Don't interrupt if already animating
          if (isAnimating) return;

          // Calculate if we need to move to this slide
          const targetIndex = index;

          if (currentIndex !== targetIndex) {
            currentIndex = targetIndex;
            updatePosition(true);
          }
        });
      });
    }

    function init() {
      setupAccessibility(); // ACCESSIBILITY: Setup ARIA attributes
      createInfiniteLoop();
      calculateDimensions();
      updatePosition(false);
      announceSlideChange(); // ACCESSIBILITY: Announce initial slide
      setupSlideFocusHandlers(); // ACCESSIBILITY: Sync slider with keyboard focus
    }

    // Event listeners
    $nextBtn.on('click', nextSlide);
    $prevBtn.on('click', prevSlide);

    // Non-active slides: pointer-events pass through to .slider-slide (see SCSS).
    // Rotate carousel here — never rely on preventDefault on <a> alone.
    $slider.on('click', '.slider-slide:not(.active)', function () {
      if (isAnimating) return;
      const $slide = $(this);
      const raw = $slide.attr('data-slide-index');
      const targetIndex = parseInt(String(raw), 10);
      if (Number.isNaN(targetIndex)) return;
      goToSlide(targetIndex);
    });

    // ACCESSIBILITY: Keyboard navigation - only when slider is focused
    let sliderHasFocus = false;

    $slider.on('focusin', function () {
      sliderHasFocus = true;
    });

    $slider.on('focusout', function () {
      sliderHasFocus = false;
    });

    $(document).on('keydown', function (e) {
      // Only respond to arrow keys when slider has focus
      if (!sliderHasFocus) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault(); // Prevent page scroll
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault(); // Prevent page scroll
        nextSlide();
      }
    });

    // Handle resize
    let resizeTimer;
    $(window).on('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        calculateDimensions();
        updatePosition(false);
      }, 250);
    });

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    $slider[0].addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    $slider[0].addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      },
      { passive: true }
    );

    function handleSwipe() {
      const swipeThreshold = 50;
      if (touchStartX - touchEndX > swipeThreshold) {
        nextSlide();
      } else if (touchEndX - touchStartX > swipeThreshold) {
        prevSlide();
      }
    }

    // Initialize
    init();
  }

  // Expose to global scope (retained for backward compatibility).
  window.initPostSlider = initPostSlider;

  // ── Auto-discover sliders from JSON data blocks ────────────────────────
  // Server-side data is passed via <script type="application/json"> blocks
  // (inert — not executed by the browser, not restricted by CSP).
  // Each block has id="cmnh-slider-data-{sliderId}" and the corresponding
  // wrapper has data-slider-id="{sliderId}".
  $('.post-slider-wrapper[data-slider-id]').each(function () {
    var sliderId = $(this).data('slider-id');
    var dataEl = document.getElementById('cmnh-slider-data-' + sliderId);

    if (dataEl) {
      try {
        var posts = JSON.parse(dataEl.textContent);
        initPostSlider(sliderId, posts);
      } catch {
        // Silently fail — slider will show static HTML content
      }
    }
  });
})(jQuery);
